import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, BookOpen, Users } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ROLE_LABEL = { administrator: "Administrator — Semua Menu", admin: "Admin — Pembelian Barang", kasir: "Kasir — Penjualan & Biaya", audit: "Audit — Lihat & Unduh Laporan" };

export default function Pengaturan() {
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [akunNama, setAkunNama] = useState("");
  const [userForm, setUserForm] = useState({ email: "", name: "", password: "", role: "kasir" });
  const [loadingAkun, setLoadingAkun] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);

  const load = useCallback(() => {
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const tambahAkun = async (e) => {
    e.preventDefault();
    setLoadingAkun(true);
    try {
      await api.post("/accounts", { nama: akunNama });
      toast.success("Akun biaya ditambahkan");
      setAkunNama("");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menambah akun"));
    } finally {
      setLoadingAkun(false);
    }
  };

  const hapusAkun = async (id) => {
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Akun dihapus");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menghapus akun"));
    }
  };

  const tambahUser = async (e) => {
    e.preventDefault();
    setLoadingUser(true);
    try {
      await api.post("/auth/register", userForm);
      toast.success("Pengguna ditambahkan");
      setUserForm({ email: "", name: "", password: "", role: "kasir" });
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menambah pengguna"));
    } finally {
      setLoadingUser(false);
    }
  };

  const hapusUser = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menghapus pengguna"));
    }
  };

  return (
    <div data-testid="pengaturan-page">
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Konfigurasi</p>
      <h1 className="mt-1 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Pengaturan</h1>

      <div className="mt-5 rounded-md border border-border bg-white p-5">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <h2 className="text-base font-bold tracking-tight">Akun Biaya (Ledger Jurnal)</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Akun biaya buatan Anda tampil di Jurnal Manual bersama akun bawaan neraca & laba rugi (Kas, Bank, Persediaan, Hutang, Modal, Pendapatan, HPP).</p>
        <form onSubmit={tambahAkun} className="mt-4 flex gap-2" data-testid="akun-form">
          <Input required value={akunNama} onChange={(e) => setAkunNama(e.target.value)} placeholder="Nama akun baru, cth: Biaya Listrik" data-testid="akun-nama-input" />
          <Button type="submit" disabled={loadingAkun} data-testid="akun-submit-button" className="shrink-0 active:scale-95 transition-transform duration-100">Tambah</Button>
        </form>
        <div className="mt-4 divide-y divide-border border-t border-border" data-testid="akun-list">
          {accounts.length === 0 && <p className="py-3 text-sm text-muted-foreground">Belum ada akun biaya.</p>}
          {accounts.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-2.5" data-testid={`akun-item-${a.id}`}>
              <p className="text-sm font-semibold">{a.nama}</p>
              <button onClick={() => hapusAkun(a.id)} data-testid={`akun-delete-${a.id}`} className="rounded-md p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150 active:scale-95">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-md border border-border bg-white p-5">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <h2 className="text-base font-bold tracking-tight">Pengguna & Hak Akses</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Administrator: semua menu · Kasir: penjualan & pengeluaran biaya · Admin: pembelian barang elektronik.</p>
        <form onSubmit={tambahUser} className="mt-4 space-y-3" data-testid="user-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input required type="email" value={userForm.email} onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Nama</Label>
              <Input value={userForm.name} onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kata Sandi</Label>
              <Input required type="password" minLength={6} value={userForm.password} onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="administrator">Administrator — Semua Menu</SelectItem>
                  <SelectItem value="kasir">Kasir — Penjualan & Biaya</SelectItem>
                  <SelectItem value="admin">Admin — Pembelian Barang</SelectItem>
                  <SelectItem value="audit">Audit — Lihat & Unduh Laporan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button type="submit" disabled={loadingUser} data-testid="user-submit-button" className="active:scale-95 transition-transform duration-100">
            {loadingUser ? "Menyimpan..." : "Tambah Pengguna"}
          </Button>
        </form>
        <div className="mt-4 divide-y divide-border border-t border-border" data-testid="user-list">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2.5" data-testid={`user-item-${u.id}`}>
              <div>
                <p className="text-sm font-semibold">{u.name || u.email}</p>
                <p className="text-xs text-muted-foreground">{u.email} · {ROLE_LABEL[u.role] || u.role}</p>
              </div>
              <button onClick={() => hapusUser(u.id)} data-testid={`user-delete-${u.id}`} className="rounded-md p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150 active:scale-95">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
